/**
 * A TINY Markdown -> HTML converter, scoped to exactly the formatting the
 * composer supports: bold, italic, strikethrough, inline code (monospace),
 * links, and bullet / numbered lists. Everything else degrades gracefully:
 *
 *   - headings (#..######)   -> bold paragraph (headings unsupported)
 *   - blockquotes (>)         -> plain paragraph
 *   - fenced ``` / code       -> the fence line is dropped (no code blocks)
 *
 * It exists so a visitor can PASTE Markdown (from docs, another chat, an AI
 * answer) and have it land as our styled rich text instead of raw markdown
 * syntax. Output is the SAME canonical subset `serialize-html` emits, so it
 * round-trips cleanly and is sanitised again on send.
 *
 * Deliberately NOT a full CommonMark parser (no nested lists, tables, images,
 * reference links, HTML passthrough) - the widget is a size-sensitive embed and
 * this covers the shapes visitors actually paste. Not for rendering untrusted
 * remote content; it's a composer input aid.
 */

/** Escape text so it's safe to drop into innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Only accept safe link schemes (mirrors `serialize-html`'s `safeHref`). */
function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return null;
  return href;
}

// Private-use sentinel wrapping a code-span index while the other inline rules
// run - a char that can't occur in pasted text and isn't a control char (so it
// passes the regex lint), unlike NUL.
const CODE_MARK = "";

/**
 * Convert one line's INLINE markdown to HTML. Code spans first (their content
 * is escaped verbatim and shielded from the other rules via a sentinel), then
 * links, then the wrap marks.
 */
export function inlineMarkdownToHtml(line: string): string {
  const codes: string[] = [];
  let s = line.replace(/`([^`\n]+)`/g, (_m, c: string) => {
    codes.push(`<code>${esc(c)}</code>`);
    return `${CODE_MARK}${codes.length - 1}${CODE_MARK}`;
  });

  s = esc(s);

  // Links: [label](href). href is scheme-checked; a rejected href degrades to
  // the bare label text.
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_m, label: string, href: string) => {
    const safe = safeHref(href);
    return safe ? `<a href="${esc(safe)}">${label}</a>` : label;
  });

  // Wrap marks. Bold before italic so **x** resolves before single *; ~~x~~
  // strike; single * / _ italic. Non-greedy, no newlines inside.
  s = s
    .replace(/\*\*([^\n]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^\n]+?)__/g, "<strong>$1</strong>")
    .replace(/~~([^\n]+?)~~/g, "<s>$1</s>")
    .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>")
    .replace(/(^|[^_])_(?!\s)([^_\n]+?)_(?!_)/g, "$1<em>$2</em>");

  // Restore the code placeholders.
  s = s.replace(
    new RegExp(`${CODE_MARK}(\\d+)${CODE_MARK}`, "g"),
    (_m, i: string) => codes[Number(i)] ?? "",
  );
  return s;
}

type Block = { kind: "p"; html: string } | { kind: "ul" | "ol"; items: string[] };

/** Convert full Markdown text to our canonical composer HTML. */
export function markdownToHtml(md: string): string {
  if (!md.trim()) return "";
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  // Buffer of consecutive non-list, non-blank lines -> one paragraph.
  let para: string[] = [];
  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push({ kind: "p", html: para.map(inlineMarkdownToHtml).join("<br>") });
    para = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushPara();
      continue;
    }
    // Fenced code delimiter - no code blocks, so drop the fence line.
    if (/^\s*```/.test(line)) continue;

    const heading = /^\s{0,3}(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      // Heading -> bold paragraph (headings unsupported).
      flushPara();
      blocks.push({ kind: "p", html: `<strong>${inlineMarkdownToHtml(heading[2])}</strong>` });
      continue;
    }
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last?.kind === "ul") last.items.push(inlineMarkdownToHtml(bullet[1]));
      else blocks.push({ kind: "ul", items: [inlineMarkdownToHtml(bullet[1])] });
      continue;
    }
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (ordered) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last?.kind === "ol") last.items.push(inlineMarkdownToHtml(ordered[1]));
      else blocks.push({ kind: "ol", items: [inlineMarkdownToHtml(ordered[1])] });
      continue;
    }
    const quote = /^\s*>\s?(.*)$/.exec(line);
    para.push(inlineMarkdownToHtml(quote ? quote[1] : line));
  }
  flushPara();

  return blocks
    .map((b) => {
      if (b.kind === "p") return `<p>${b.html}</p>`;
      const items = b.items.map((it) => `<li>${it}</li>`).join("");
      return `<${b.kind}>${items}</${b.kind}>`;
    })
    .join("");
}

/** Heuristic: does this plain text look like Markdown worth converting on
 *  paste? Conservative - only fires on clear syntax so a normal message with a
 *  stray * or - isn't mangled. */
export function looksLikeMarkdown(text: string): boolean {
  return [
    /\*\*[^*\n]+\*\*/, // **bold**
    /__[^_\n]+__/, // __bold__
    /~~[^~\n]+~~/, // ~~strike~~
    /`[^`\n]+`/, // `code`
    /\[[^\]\n]+\]\([^)\s]+\)/, // [text](url)
    /^\s{0,3}#{1,6}\s+\S/m, // # heading
    /^\s*[-*+]\s+\S/m, // - list
    /^\s*\d+\.\s+\S/m, // 1. list
  ].some((re) => re.test(text));
}
